import { addDoc, collection, doc, getDoc, getDocs, query, where, serverTimestamp } from "firebase/firestore";
import {
  Search,
  X,
  ChevronRight,
  ChevronLeft,
  Eye,
  Users,
  CheckSquare,
  Clock,
  CircleCheck,
  Download,
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import FindCaseStepper from "../../components/crm/FindCaseStepper";
import { auth, db } from "../../firebase";
import { apiRequest } from "../../services/fetchCaseApi";
import { getFullCourtName } from "../../utils/courtMapping";
import InfoModal from "../../components/InfoModal";
import { useAuth } from "../../context/AuthContext";
import { getMessaging, getToken } from "firebase/messaging";
import featureGatingService from "../../services/featureGatingService";
import CaseLimitDialog from "../../components/CaseLimitDialog";
import { persistOrderPdfs, saveOrderPdfsToFiles, extractAllPartyNames } from "./highCourtOrderFiles";
import { STATUS_PILL_BASE, STATUS_PILL_SOFT } from "../../styles/statusPill";

const HC_LIST_STATE_PREFIX = "hc_caseList_viewState:";

function CaseListScreen() {
  const messaging = getMessaging();
  const location = useLocation();
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [selectedCases, setSelectedCases] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [duplicateCases, setDuplicateCases] = useState([]);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [exportProgress, setExportProgress] = useState({
    total: 0,
    exported: 0,
    currentCase: null,
    startTime: null,
    failedCases: []
  });
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [showFailedExportModal, setShowFailedExportModal] = useState(false);
  const [failedExportMessage, setFailedExportMessage] = useState("");
  const [loadingCaseCino, setLoadingCaseCino] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  // Case limit states
  const [showCaseLimitDialog, setShowCaseLimitDialog] = useState(false);
  const [caseLimitData, setCaseLimitData] = useState({
    currentCount: 0,
    maxLimit: 0,
    planName: ''
  });

  // Function to open the modal
  const showInfo = () => {
    setIsInfoModalOpen(true);
  };

  // Function to close the modal (passed to InfoModal)
  const closeInfoModal = () => {
    setIsInfoModalOpen(false);
  };

  useEffect(() => {
    setIsLoadingData(true);
    if (location.state && location.state.data && location.state.data.data) {
      setCaseData(location.state.data.data);
      setIsLoadingData(false);
    } else {
      setIsLoadingData(false);
    }
  }, [location.state]);

  console.log("location state", location.state);

  const cases = caseData?.con || [];

  // Filter cases by search query (petitioner, respondent, extra party, case number, cino, type)
  const filteredCases = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return cases;
    return cases.filter((c) => {
      const fields = [
        c.pet_name,
        c.res_name,
        c.extra_party,
        c.lextra_party,
        c.case_no,
        c.cino,
        c.type_name,
        c.lpet_name,
        c.lres_name,
      ];
      return fields.some(
        (f) => typeof f === "string" && f.toLowerCase().includes(q)
      );
    });
  }, [cases, searchQuery]);

  // Summary counts for the stat cards — derived from the same list, no extra fetch.
  const statusCounts = useMemo(() => {
    const pending = cases.filter((c) => c.date_of_decision == null).length;
    return { total: cases.length, pending, disposed: cases.length - pending };
  }, [cases]);

  const getHighCourtDetailsCaseNumber = (caseItem) => {
    const shortCaseNumber = caseItem?.case_no2;
    if (shortCaseNumber !== undefined && shortCaseNumber !== null && `${shortCaseNumber}`.trim() !== "") {
      return String(shortCaseNumber);
    }
    return caseItem?.case_no || "";
  };

  const getHighCourtDetailsCourtCode = (searchParams = {}) => {
    return searchParams?.court_code || searchParams?.court_complex_code || "";
  };

  // Restore persisted view state (page + search) on mount so the user lands
  // back where they were after exporting or viewing case details.
  const [currentPage, setCurrentPage] = useState(1);
  const [casesPerPage] = useState(10);
  const [viewStateRestored, setViewStateRestored] = useState(false);

  // location.key changes per navigation entry — using it as part of the
  // storage key ties saved state to THIS visit of the list, so back-nav
  // restores it, but a brand-new search starts fresh.
  const stateStorageKey = `${HC_LIST_STATE_PREFIX}${location.key || "default"}`;

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(stateStorageKey);
      if (raw) {
        const saved = JSON.parse(raw);
        if (saved && typeof saved === "object") {
          if (typeof saved.searchQuery === "string") {
            setSearchQuery(saved.searchQuery);
          }
          if (typeof saved.currentPage === "number" && saved.currentPage > 0) {
            setCurrentPage(saved.currentPage);
          }
        }
      }
    } catch (e) {
      // ignore corrupt state
    }
    setViewStateRestored(true);
  }, [stateStorageKey]);

  // Persist whenever page or search changes (after initial restore)
  useEffect(() => {
    if (!viewStateRestored) return;
    try {
      sessionStorage.setItem(
        stateStorageKey,
        JSON.stringify({ currentPage, searchQuery })
      );
    } catch (e) {
      // ignore quota errors
    }
  }, [currentPage, searchQuery, viewStateRestored, stateStorageKey]);

  const indexOfLastCase = currentPage * casesPerPage;
  const indexOfFirstCase = indexOfLastCase - casesPerPage;
  const currentCases = filteredCases.slice(indexOfFirstCase, indexOfLastCase);
  const totalPages = Math.max(1, Math.ceil(filteredCases.length / casesPerPage));

  // If the filtered set shrinks below the current page, snap back to last valid page
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    // Don't clear selections when changing pages - allow bulk selection across pages
  };

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setCurrentPage(1);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      handlePageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      handlePageChange(currentPage + 1);
    }
  };

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5; // Show 5 page numbers at a time

    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    // Adjust startPage if we're near the end
    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return pageNumbers;
  };

  // Stable key per row — fall back to case_no + cino composite when cino missing
  const getCaseKey = (caseItem) =>
    caseItem?.cino || `${caseItem?.case_no || ""}__${caseItem?.case_no2 || ""}__${caseItem?.orcase || ""}`;

  const handleCaseSelect = (caseItem) => {
    const key = getCaseKey(caseItem);
    const newSelectedCases = new Set(selectedCases);
    if (newSelectedCases.has(key)) {
      newSelectedCases.delete(key);
    } else {
      newSelectedCases.add(key);
    }
    setSelectedCases(newSelectedCases);
    // Update selectAll based on whether all currently-visible (filtered) cases are selected
    setSelectAll(
      filteredCases.length > 0 &&
        filteredCases.every((c) => newSelectedCases.has(getCaseKey(c)))
    );
  };

  const handleSelectAll = () => {
    if (selectAll) {
      // Deselect all (only the ones in current filtered view)
      const newSelectedCases = new Set(selectedCases);
      filteredCases.forEach((c) => newSelectedCases.delete(getCaseKey(c)));
      setSelectedCases(newSelectedCases);
      setSelectAll(false);
    } else {
      // Select all cases in the current filtered view
      const newSelectedCases = new Set(selectedCases);
      filteredCases.forEach((c) => newSelectedCases.add(getCaseKey(c)));
      setSelectedCases(newSelectedCases);
      setSelectAll(true);
    }
  };

  const handlePageSelectAll = () => {
    // Select/deselect only current page
    const currentPageKeys = currentCases.map((c) => getCaseKey(c));
    const newSelectedCases = new Set(selectedCases);

    const allCurrentSelected = currentPageKeys.every((k) => selectedCases.has(k));

    if (allCurrentSelected) {
      currentPageKeys.forEach((k) => newSelectedCases.delete(k));
    } else {
      currentPageKeys.forEach((k) => newSelectedCases.add(k));
    }

    setSelectedCases(newSelectedCases);
    setSelectAll(
      filteredCases.length > 0 &&
        filteredCases.every((c) => newSelectedCases.has(getCaseKey(c)))
    );
  };

  // Keep "Select All" checkbox in sync as the filter changes
  useEffect(() => {
    if (filteredCases.length === 0) {
      setSelectAll(false);
      return;
    }
    setSelectAll(filteredCases.every((c) => selectedCases.has(getCaseKey(c))));
  }, [filteredCases, selectedCases]);

  const convertDateFormat = (originalDate) => {
    if (!originalDate) return "";

    // Try to parse various date formats
    const dateFormats = [
      /(\d{1,2})(st|nd|rd|th)\s+(January|February|March|April|May|June|July|August|September|October|November|December)\s+(\d{4})/i,
      /(\d{1,2})\/(\d{1,2})\/(\d{4})/,
    ];

    for (const format of dateFormats) {
      const match = originalDate.match(format);
      if (match) {
        let day, month, year;

        if (match.length === 5) {
          // Handle format like "1st January 2023"
          day = match[1].padStart(2, "0");
          const monthNames = {
            January: "01",
            February: "02",
            March: "03",
            April: "04",
            May: "05",
            June: "06",
            July: "07",
            August: "08",
            September: "09",
            October: "10",
            November: "11",
            December: "12",
          };
          month =
            monthNames[
            match[3].charAt(0).toUpperCase() + match[3].slice(1).toLowerCase()
            ];
          year = match[4];
        } else {
          // Handle format like "dd/mm/yyyy"
          day = match[1].padStart(2, "0");
          month = match[2].padStart(2, "0");
          year = match[3];
        }

        return `${day}/${month}/${year}`;
      }
    }

    return originalDate;
  };

  function convertHyphenToSlash(originalDate) {
    if (!originalDate) {
      return "";
    }

    const datePattern = /^\d{2}-\d{2}-\d{4}$/;
    if (!datePattern.test(originalDate)) {
      return originalDate;
    }

    // Replace hyphens with slashes
    return originalDate.replace(/-/g, "/");
  }

  // Extract advocate name(s) from the advocate string
  const extractAdvocateNames = (advocateString) => {
    if (!advocateString || typeof advocateString !== 'string') return [];

    // Split by "Advocate-" or "Advocate -" (with space)
    const parts = advocateString.split(/Advocate\s*-\s*/);

    if (parts.length > 1) {
      // Extract advocate names after "Advocate-"
      const advocatesPart = parts[1].trim();

      // Split by comma to handle multiple advocates
      const advocates = advocatesPart
        .split(',')
        .map(adv => {
          // Remove leading numbers like "1)", "2)", etc.
          return adv.trim().replace(/^\d+\)\s*/, '');
        })
        .filter(adv =>
          adv &&
          adv !== '' &&
          adv !== '1123' &&
          !adv.toUpperCase().includes('AND') && // Filter out "AND X OTHERS"
          adv.length > 1 // Filter out single characters
        );

      return advocates;
    }

    return [];
  };

  // Extract all unique advocates from petitioner and respondent advocate arrays
  // Extract all unique advocates from petitioner advocate array
  const extractPetitionerAdvocates = (petitionerAdvocateArray) => {
    const advocates = new Set();
    if (Array.isArray(petitionerAdvocateArray)) {
      petitionerAdvocateArray.forEach(advString => {
        const advocateNames = extractAdvocateNames(advString);
        advocateNames.forEach(adv => advocates.add(adv));
      });
    }
    return Array.from(advocates);
  };

  // Extract all unique advocates from respondent advocate array
  const extractRespondentAdvocates = (respondentAdvocateArray) => {
    const advocates = new Set();
    if (Array.isArray(respondentAdvocateArray)) {
      respondentAdvocateArray.forEach(advString => {
        const advocateNames = extractAdvocateNames(advString);
        advocateNames.forEach(adv => advocates.add(adv));
      });
    }
    return Array.from(advocates);
  };

  const [error, setError] = useState("");
  const [userMobile, setUserMobile] = useState("");

  const { globalUser } = useAuth();
  const userId = globalUser?.uid;

  useEffect(() => {
    const fetchData = async () => {
      if (!userId) return;
      setError("");
      try {
        const q = query(
          collection(db, "pending"),
          where("owner", "==", userId)
        );
        const querySnapshot = await getDocs(q);
        const cases = querySnapshot.docs.map((doc) => doc.data().caseNo);
        //  setCaseNumbers(cases);

        const userQuery = query(
          collection(db, "lawyers"),
          where("uid", "==", userId)
        );
        const userDocSnapshot = await getDocs(userQuery);
        if (!userDocSnapshot.empty) {
          const userData = userDocSnapshot.docs[0].data();
          console.log("User data:", userData);
          console.log("User mobile:", userData.mobileWithCountryCode);
          setUserMobile(userData.mobileWithCountryCode || "");
        } else {
          console.warn("User document not found for UID:", userId);
          setUserMobile("");
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load required data");
      }
    };
    fetchData();
  }, [userId]);

  // Check case limit before exporting using centralized service
  const checkCaseLimitBeforeExport = async (numberOfCasesToExport, targetCollection = 'pending') => {
    if (!globalUser?.uid) return false;

    const result = await featureGatingService.checkCaseLimitBeforeExport(
      globalUser.uid,
      numberOfCasesToExport,
      targetCollection
    );

    if (!result.canProceed) {
      console.log('⚠️ Case limit would be exceeded, showing dialog...');
      setCaseLimitData({
        currentCount: result.currentCount,
        maxLimit: result.maxLimit,
        planName: result.planName
      });
      setShowCaseLimitDialog(true);
      return false;
    }

    return true;
  };


  // const exportSelectedCases = async () => {
  //   try {
  //     const user = auth.currentUser;
  //     if (!user) {
  //       alert("User not authenticated");
  //       return;
  //     }

  //     const cases = caseData?.con || [];
  //     const selectedCaseIndices = Array.from(selectedCases || []);

  //     if (selectedCaseIndices.length === 0) {
  //       alert("No cases selected to export");
  //       return;
  //     }

  //     // Show export dialog immediately
  //     setExporting(true);
  //     setExportProgress({
  //       total: selectedCaseIndices.length,
  //       exported: 0,
  //       currentCase: "Preparing to export cases...",
  //       startTime: Date.now(),
  //       failedCases: []
  //     });

  //     // Small delay to ensure modal renders
  //     await new Promise(resolve => setTimeout(resolve, 100));

  //     // Check for duplicates after showing the modal
  //     const duplicatesFound = [];
  //     const casesToExport = [];

  //     for (const index of selectedCaseIndices) {
  //       try {
  //         const caseItem = cases[index];
  //         if (!caseItem) continue;

  //         // Update current case being processed
  //         setExportProgress(prev => ({
  //           ...prev,
  //           currentCase: `Checking case: ${caseItem.case_no}`
  //         }));

  //         // Small delay to make UI updates visible
  //         await new Promise(resolve => setTimeout(resolve, 50));

  //         // Fetch case details to get filing number
  //         const searchParams = location?.state?.searchParams;
  //         if (!searchParams) continue;

  //         const response = await apiRequest(
  //           "/case/details/highcourt",
  //           {
  //             hcservices_sessid: searchParams.cookies?.HCSERVICES_SESSID || "",
  //             jsession_value: searchParams.cookies?.JSESSIONID || "",
  //             court_code: searchParams.court_complex_code || "",
  //             state_code: searchParams.state_code || "",
  //             court_complex_code: searchParams.court_complex_code || "",
  //             case_no: caseItem?.case_no || "",
  //             cino: caseItem?.cino || "",
  //             appFlag: "",
  //           }
  //         );

  //         const caseDetails = response?.data;
  //         const filingNumber = caseDetails?.caseDetails?.["Filing Number"] || "";

  //         if (filingNumber) {
  //           // Determine collection based on status
  //           const statusToCheck = searchParams.status?.toLowerCase() || "unknown";
  //           const collectionsToCheck = statusToCheck === "unknown"
  //             ? ["pending", "disposed", "decided"]
  //             : [statusToCheck];

  //           let isDuplicate = false;
  //           for (const collectionName of collectionsToCheck) {
  //             const q = query(
  //               collection(db, collectionName),
  //               where("caseNo", "==", filingNumber),
  //               where("owner", "==", user.uid)
  //             );
  //             const snapshot = await getDocs(q);

  //             if (!snapshot.empty) {
  //               duplicatesFound.push({
  //                 caseNo: filingNumber,
  //                 collection: collectionName,
  //                 caseItem: caseItem
  //               });
  //               isDuplicate = true;
  //               break;
  //             }
  //           }

  //           if (!isDuplicate) {
  //             casesToExport.push({ index, caseItem, caseDetails });
  //           }
  //         }
  //       } catch (error) {
  //         console.error("Error checking for duplicate:", error);
  //         casesToExport.push({ index, caseItem: cases[index], caseDetails: null });
  //       }
  //     }

  //     // If all selected cases are duplicates, show modal and stop
  //     if (duplicatesFound.length === selectedCaseIndices.length) {
  //       setExporting(false);
  //       setDuplicateCases(duplicatesFound);
  //       setShowDuplicateModal(true);
  //       setExportProgress({
  //         total: 0,
  //         exported: 0,
  //         currentCase: null,
  //         startTime: null,
  //         failedCases: []
  //       });
  //       return;
  //     }

  //     // If some duplicates found, show warning but continue with non-duplicates
  //     if (duplicatesFound.length > 0) {
  //       setExporting(false);
  //       const proceed = window.confirm(
  //         `${duplicatesFound.length} case(s) already exist in your profile. Do you want to export the remaining ${casesToExport.length} case(s)?`
  //       );
  //       if (!proceed) {
  //         setExportProgress({
  //           total: 0,
  //           exported: 0,
  //           currentCase: null,
  //           startTime: null,
  //           failedCases: []
  //         });
  //         return;
  //       }
  //       setExporting(true);
  //     }

  //     // Update progress for actual export
  //     setExportProgress({
  //       total: casesToExport.length,
  //       exported: 0,
  //       currentCase: "Starting export process...",
  //       startTime: Date.now(),
  //       failedCases: []
  //     });

  //     let successfulExports = 0;
  //     let failedExports = 0;
  //     const failedCasesList = [];

  //     for (const { index, caseItem, caseDetails: preLoadedDetails } of casesToExport) {
  //       try {
  //         // Update current case being exported
  //         setExportProgress(prev => ({
  //           ...prev,
  //           currentCase: `Exporting: ${caseItem.case_no}`,
  //           exported: prev.exported
  //         }));

  //         // Use pre-loaded details if available, otherwise fetch
  //         let caseDetails = preLoadedDetails;
  //         const searchParams = location?.state?.searchParams;

  //         if (!caseDetails) {
  //           const response = await apiRequest(
  //             "/case/details/highcourt",
  //             {
  //               hcservices_sessid: searchParams.cookies?.HCSERVICES_SESSID || "",
  //               jsession_value: searchParams.cookies?.JSESSIONID || "",
  //               court_code: searchParams.court_complex_code || "",
  //               state_code: searchParams.state_code || "",
  //               court_complex_code: searchParams.court_complex_code || "",
  //               case_no: caseItem?.case_no || "",
  //               cino: caseItem?.cino || "",
  //               appFlag: "",
  //             }
  //           );
  //           caseDetails = response?.data;
  //         }

  //         if (!caseDetails) {
  //           console.warn(`No case details received for case: ${caseItem?.case_no}`);
  //           failedExports++;
  //           failedCasesList.push(caseItem?.case_no || 'Unknown');
  //           continue;
  //         }

  //         // Get full court name using the mapping with null check
  //         const courtName = searchParams.state_code
  //           ? getFullCourtName(searchParams.state_code)
  //           : "Unknown Court";

  //         // Prepare case data for Firestore with proper null checks
  //         const caseStatus = searchParams.status?.toLowerCase() || "unknown";

  //         const caseFiledDate = convertHyphenToSlash(
  //           caseDetails?.caseDetails?.["Filing Date"] || ""
  //         );

  //         const nextHearingDate = convertDateFormat(
  //           caseDetails?.caseStatus?.["Next Hearing Date"] || ""
  //         );

  //         // Safe access to hearing history
  //         const hearingHistory = caseDetails?.hearingHistory;
  //         const hearingHistoryLength = Array.isArray(hearingHistory) ? hearingHistory.length : 0;

  //         // Now previous hearing date will be in the last object of the array hearingHistory
  //         let previousHearingDate = "";
  //         if (hearingHistoryLength > 0 && hearingHistory[hearingHistoryLength - 1]) {
  //           previousHearingDate = convertHyphenToSlash(
  //             hearingHistory[hearingHistoryLength - 1].hearingDate || ""
  //           );
  //         }

  //         // Safe extraction of party names with proper fallbacks
  //         const extractPartyName = (advocateString) => {
  //           try {
  //             if (!advocateString || typeof advocateString !== 'string') return "N/A";
  //             const parts = advocateString.split("Advocate-");
  //             return parts.length > 0 ? parts[0].trim().replace(/^\d+\) /, "") : "N/A";
  //           } catch (error) {
  //             console.error("Error extracting party name:", error);
  //             return "N/A";
  //           }
  //         };

  //         const petitionerAdvocate = Array.isArray(caseDetails?.petitionerAdvocate) && caseDetails.petitionerAdvocate.length > 0
  //           ? caseDetails.petitionerAdvocate[0]
  //           : "";

  //         const respondentAdvocate = Array.isArray(caseDetails?.respondentAdvocate) && caseDetails.respondentAdvocate.length > 0
  //           ? caseDetails.respondentAdvocate[0]
  //           : "";

  //         const partyName = extractPartyName(petitionerAdvocate);
  //         const adverseParty = extractPartyName(respondentAdvocate);

  //         const caseDataToExport = {
  //           caseNo: caseDetails?.caseDetails?.["Registration Number"] || "",
  //           caseType: caseDetails?.caseStatus?.["Judicial Branch"] || "",
  //           caseFiledDate: caseFiledDate,
  //           nextHearingDate: nextHearingDate,
  //           previousHearingDate: previousHearingDate,
  //           courtName: courtName,
  //           petitionerName: partyName,
  //           partyContactNo: "",
  //           respondentName: adverseParty,
  //           appearingFor: "",
  //           team: [userId],
  //           files: [],
  //           casenotes: "",
  //           status: caseStatus,
  //           owner: user.uid,
  //           caseHistory: caseDetails?.hearingHistory || [], // Add hearingHistory
  //           orders: caseDetails?.orders || [], // Add orders
  //         };

  //         // Add to Firestore
  //         const caseRef = await addDoc(collection(db, caseStatus), caseDataToExport);
  //         console.log("Case added to Firestore:", caseRef.id);

  //         // Get FCM token
  //         let fcmToken = "";
  //         try {
  //           console.log("Attempting to get FCM token...");
  //           fcmToken = await getToken(messaging, {
  //             vapidKey: "BOSi0E-WalUbqOuB5su9XW2J39ot0D4x-fSPRYo_AxMVloqe7jIkpYXf5J007lXs1D_ZZB9YH1Hc0X-7jQrqR38",
  //           });
  //           console.log("FCM token obtained:", fcmToken);
  //         } catch (err) {
  //           console.error("Error getting FCM token:", err);
  //           console.warn("Could not get FCM token. Push notifications might not work:", err.message);
  //         }

  //         // Schedule notification if hearing date exists
  //         if (nextHearingDate) {
  //           try {
  //             const eventDateStr = nextHearingDate.split("/").reverse().join("-");
  //             const eventDate = new Date(eventDateStr);
  //             if (isNaN(eventDate.getTime())) {
  //               console.warn("Invalid hearing date format:", nextHearingDate);
  //             } else {
  //               // Set event time to 8:00 AM
  //               eventDate.setHours(8, 0, 0, 0);
  //               const eventTimestamp = eventDate.getTime();

  //               // Create event
  //               const eventData = {
  //                 "Case No": caseDetails?.caseDetails?.["Registration Number"] || "",
  //                 date: eventDate.toISOString().split("T")[0],
  //                 id: Math.floor(Math.random() * 90000) + 10000,
  //                 mobile: userMobile || null,
  //                 time: "08:00 AM",
  //                 time1: eventTimestamp,
  //                 title: `${partyName} VS ${adverseParty}`,
  //                 isScheduled: eventTimestamp > Date.now(),
  //                 status: caseStatus,
  //               };
  //               const eventRef = await addDoc(collection(db, "lawyers", user.uid, "events"), eventData);
  //               const eventId = eventRef.id;
  //               console.log("Event created with ID:", eventId);

  //               // Calculate reminder time: one day before at 8:00 AM
  //               const reminderDate = new Date(eventDate);
  //               reminderDate.setDate(reminderDate.getDate() - 1);
  //               reminderDate.setHours(8, 0, 0, 0);
  //               const now = new Date();

  //               if (reminderDate > now) {
  //                 const reminderDoc = {
  //                   eventId: eventId,
  //                   reminderTime: reminderDate, // Will be converted to Timestamp by Firestore
  //                   eventTitle: `${partyName} VS ${adverseParty}`,
  //                   caseNo: caseDetails?.caseDetails?.["Registration Number"] || "",
  //                   status: "pending",
  //                   createdAt: new Date(),
  //                   scheduledBy: user.uid,
  //                   recipientId: user.uid, // Reminder for the current user
  //                 };
  //                 await addDoc(collection(db, "eventReminders"), reminderDoc);
  //                 console.log("Default reminder created for the event.");
  //               } else {
  //                 console.log("Reminder time is in the past, not creating reminder.");
  //               }
  //             }
  //           } catch (notificationError) {
  //             console.error("Notification setup error:", notificationError);
  //           }
  //         }

  //         successfulExports++;

  //         // Update export progress
  //         setExportProgress((prev) => ({
  //           ...prev,
  //           exported: prev.exported + 1,
  //           currentCase: `Completed: ${caseItem.case_no}`
  //         }));

  //         // Small delay to make progress visible
  //         await new Promise(resolve => setTimeout(resolve, 100));

  //       } catch (caseError) {
  //         console.error(`Error exporting case at index ${index}:`, caseError);
  //         failedExports++;
  //         failedCasesList.push(caseItem?.case_no || 'Unknown');

  //         // Update progress even for failed cases
  //         setExportProgress((prev) => ({
  //           ...prev,
  //           exported: prev.exported + 1,
  //           failedCases: [...prev.failedCases, caseItem?.case_no || 'Unknown'],
  //           currentCase: `Failed: ${caseItem?.case_no}`
  //         }));

  //         // Small delay to make progress visible
  //         await new Promise(resolve => setTimeout(resolve, 100));
  //       }
  //     }

  //     // Show completion message briefly before closing
  //     await new Promise(resolve => setTimeout(resolve, 500));

  //     // Show results
  //     if (successfulExports > 0) {
  //       const message = failedExports > 0
  //         ? `Successfully exported ${successfulExports} cases. ${failedExports} cases failed to export.`
  //         : `Successfully exported ${successfulExports} cases!`;

  //       console.log(message);
  //       showInfo(); // Assuming this shows a success modal
  //     } else {
  //       alert("No cases were exported successfully. Please check the console for errors.");
  //     }

  //   } catch (error) {
  //     console.error("Export error:", error);
  //     alert(`Error exporting cases: ${error.message}`);
  //   } finally {
  //     setExporting(false);
  //     // Reset progress
  //     setExportProgress({
  //       total: 0,
  //       exported: 0,
  //       currentCase: null,
  //       startTime: null,
  //       failedCases: []
  //     });
  //   }
  // };

  // Modified exportSelectedCases function with sequential processing
// This shows only the changed function - integrate this into your existing CaseListScreen component

const exportSelectedCases = async () => {
  try {
    const user = auth.currentUser;
    if (!user) {
      alert("User not authenticated");
      return;
    }

    const cases = caseData?.con || [];
    const selectedKeys = new Set(selectedCases || []);

    // Resolve selected case objects from the full list, preserving on-screen order
    const selectedCaseItems = cases.filter((c) => selectedKeys.has(getCaseKey(c)));

    if (selectedCaseItems.length === 0) {
      alert("No cases selected to export");
      return;
    }

    // Check case limit before proceeding
    const searchParams = location?.state?.searchParams;
    const bulkStatus = searchParams?.status?.toLowerCase() || "pending";
    const bulkTargetCollection = bulkStatus === "disposed" ? "disposed" : "pending";
    const canProceed = await checkCaseLimitBeforeExport(selectedCaseItems.length, bulkTargetCollection);
    if (!canProceed) {
      return; // Stop if limit exceeded
    }

    // Show export dialog immediately
    setExporting(true);
    setExportProgress({
      total: selectedCaseItems.length,
      exported: 0,
      currentCase: "Preparing to export cases...",
      startTime: Date.now(),
      failedCases: []
    });

    // Small delay to ensure modal renders
    await new Promise(resolve => setTimeout(resolve, 100));

    const duplicatesFound = [];
    let successfulExports = 0;
    let failedExports = 0;
    const failedCasesList = [];

    // Track latest cookies - HCSERVICES_SESSID updates per response, JSESSION stays from original captcha
    const origCookies = location.state?.searchParams?.cookies || {};
    let latestHcSessId = location.state?.data?.cookies?.HCSERVICES_SESSID || origCookies?.HCSERVICES_SESSID || "";
    const jsessionVal = origCookies?.JSESSION || origCookies?.JSESSIONID || "";

    // Get current pending case count for subscription tracking
    const pendingQuery = query(
      collection(db, "pending"),
      where("owner", "==", user.uid)
    );
    const pendingSnapshot = await getDocs(pendingQuery);
    let currentPendingCount = pendingSnapshot.size;

    // Process cases one by one - check and export immediately
    for (const caseItem of selectedCaseItems) {
      try {
        if (!caseItem) continue;

        // Update current case being processed
        setExportProgress(prev => ({
          ...prev,
          currentCase: `Checking case: ${caseItem.case_no}`,
          exported: prev.exported
        }));

        // Small delay to make UI updates visible
        await new Promise(resolve => setTimeout(resolve, 50));

        // Fetch case details to get filing number
        const searchParams = location?.state?.searchParams;
        if (!searchParams) continue;

        const detailsPayload = {
          hcservices_sessid: latestHcSessId,
          jsession_value: jsessionVal,
          court_code: getHighCourtDetailsCourtCode(searchParams),
          state_code: searchParams.state_code || "",
          court_complex_code: searchParams.court_complex_code || "",
          case_no: getHighCourtDetailsCaseNumber(caseItem),
          cino: caseItem?.cino || "",
          appFlag: "",
        };

        const response = await apiRequest(
          "/case/details/highcourt",
          detailsPayload
        );

        // Check for error response from API
        if (response?.error) {
          failedExports++;
          failedCasesList.push(caseItem?.case_no || 'Unknown');
          continue;
        }

        // Update HCSERVICES_SESSID from response (it rotates per request)
        if (response?.cookies?.HCSERVICES_SESSID) {
          latestHcSessId = response.cookies.HCSERVICES_SESSID;
        }

        const caseDetails = response?.data;
        const caseNo = caseDetails?.caseDetails?.["Registration Number"] || "";

        // Check for duplicate
        let isDuplicate = false;
        if (caseNo) {
          // Determine collection based on status
          const statusToCheck = searchParams.status?.toLowerCase() || "unknown";
          const collectionsToCheck = statusToCheck === "unknown"
            ? ["pending", "disposed", "decided"]
            : [statusToCheck];

          for (const collectionName of collectionsToCheck) {
            const q = query(
              collection(db, collectionName),
              where("caseNo", "==", caseNo),
              where("owner", "==", user.uid)
            );
            const snapshot = await getDocs(q);

            if (!snapshot.empty) {
              duplicatesFound.push({
                caseNo: caseNo,
                collection: collectionName,
                caseItem: caseItem
              });
              isDuplicate = true;

              // Update progress even for duplicates
              setExportProgress(prev => ({
                ...prev,
                exported: prev.exported + 1,
                currentCase: `Skipped duplicate: ${caseItem.case_no}`
              }));

              break;
            }
          }
        }

        // If not duplicate, export immediately
        if (!isDuplicate) {
          // Update current case being exported
          setExportProgress(prev => ({
            ...prev,
            currentCase: `Exporting: ${caseItem.case_no}`
          }));

          if (!caseDetails) {
            console.warn(`No case details received for case: ${caseItem?.case_no}`);
            failedExports++;
            failedCasesList.push(caseItem?.case_no || 'Unknown');
            
            // Update progress for failed case
            setExportProgress(prev => ({
              ...prev,
              exported: prev.exported + 1,
              failedCases: [...prev.failedCases, caseItem?.case_no || 'Unknown'],
              currentCase: `Failed: ${caseItem.case_no}`
            }));
            continue;
          }

          // Get full court name using the mapping with null check
          const courtName = searchParams.state_code
            ? getFullCourtName(searchParams.state_code)
            : "Unknown Court";

          // Prepare case data for Firestore with proper null checks
          const caseStatus = searchParams.status?.toLowerCase() || "unknown";

          const caseFiledDate = convertHyphenToSlash(
            caseDetails?.caseDetails?.["Filing Date"] || ""
          );

          const nextHearingDate = convertDateFormat(
            caseDetails?.caseStatus?.["Next Hearing Date"] || ""
          );

          // Safe access to hearing history
          const hearingHistory = caseDetails?.hearingHistory;
          const hearingHistoryLength = Array.isArray(hearingHistory) ? hearingHistory.length : 0;

          // Now previous hearing date will be in the last object of the array hearingHistory
          let previousHearingDate = "";
          if (hearingHistoryLength > 0 && hearingHistory[1]) {
            previousHearingDate = convertHyphenToSlash(
              hearingHistory[1].hearingDate || ""
            );
          }

          // Safe extraction of party names with proper fallbacks
          const extractPartyName = (advocateString) => {
            try {
              if (!advocateString || typeof advocateString !== 'string') return "N/A";
              const parts = advocateString.split("Advocate-");
              return parts.length > 0 ? parts[0].trim().replace(/^\d+\) /, "") : "N/A";
            } catch (error) {
              console.error("Error extracting party name:", error);
              return "N/A";
            }
          };

          const petitionerAdvocate = Array.isArray(caseDetails?.petitionerAdvocate) && caseDetails.petitionerAdvocate.length > 0
            ? caseDetails.petitionerAdvocate[0]
            : "";

          const respondentAdvocate = Array.isArray(caseDetails?.respondentAdvocate) && caseDetails.respondentAdvocate.length > 0
            ? caseDetails.respondentAdvocate[0]
            : "";

          const partyName = extractPartyName(petitionerAdvocate);
          const adverseParty = extractPartyName(respondentAdvocate);

          // Extract advocate names from full arrays
          const allPetitionerAdvocates = extractPetitionerAdvocates(caseDetails?.petitionerAdvocate);
          const allRespondentAdvocates = extractRespondentAdvocates(caseDetails?.respondentAdvocate);

          // Extract every petitioner/respondent name (including "...AND X OTHERS")
          // so additional parties survive export instead of only the first one.
          const allPetitioners = extractAllPartyNames(caseDetails?.petitionerAdvocate);
          const allRespondents = extractAllPartyNames(caseDetails?.respondentAdvocate);

          // Persist each order's PDF to Firebase Storage now, while the eCourts
          // session is still fresh, so the orders can be mirrored into Files.
          const orderCookies = {
            HCSERVICES_SESSID: latestHcSessId,
            JSESSION: jsessionVal,
          };
          const ordersWithPdfs = await persistOrderPdfs(
            caseDetails?.orders || [],
            orderCookies,
            user.uid,
            caseNo
          );

          // Get search payload from localStorage and combine with details payload
          const searchPayloadStr = localStorage.getItem('hc_search_payload');
          const combinedPayload = {
            searchPayload: searchPayloadStr ? JSON.parse(searchPayloadStr) : null,
            detailsPayload: detailsPayload,
            exportedAt: new Date().toISOString(),
            caseexportedfrom: "HighCourt",
          };

          // Get subscription metadata if case is beyond free tier and adding to pending
          let subscriptionMetadata = null;
          if (caseStatus === "pending") {
            subscriptionMetadata = await featureGatingService.getSubscriptionMetadataForNewCase(
              user.uid,
              currentPendingCount
            );

            console.log('📦 [High Court Bulk] Subscription metadata for new case:', {
              currentPendingCount,
              hasMetadata: !!subscriptionMetadata
            });
          }

          const caseDataToExport = {
            caseNo: caseDetails?.caseDetails?.["Registration Number"] || "",
            filingNumber: caseDetails?.caseDetails?.["Filing Number"] || "",
            caseType: caseDetails?.caseStatus?.["Judicial Branch"] || "",
            caseFiledDate: caseFiledDate,
            nextHearingDate: nextHearingDate,
            previousHearingDate: previousHearingDate,
            courtName: courtName,
            petitionerName: partyName,
            allPetitioners: allPetitioners, // All petitioners (main + others)
            partyContactNo: "",
            respondentName: adverseParty,
            allRespondents: allRespondents, // All respondents (main + others)
            appearingFor: "",
            petitionerAdvocate: allPetitionerAdvocates.length > 0 ? allPetitionerAdvocates[0] : "", // First petitioner advocate
            respondentAdvocate: allRespondentAdvocates.length > 0 ? allRespondentAdvocates[0] : "", // First respondent advocate
            allPetitionerAdvocates: allPetitionerAdvocates, // All petitioner advocates
            allRespondentAdvocates: allRespondentAdvocates, // All respondent advocates
            team: [userId],
            files: [],
            casenotes: "",
            status: caseStatus,
            owner: user.uid,
            caseHistory: caseDetails?.hearingHistory || [], // Add hearingHistory
            orders: ordersWithPdfs, // Orders, each enriched with a persisted pdfStorageUrl when available
            categoryDetails: caseDetails?.categoryDetails || {}, // Add category details
            payload: combinedPayload,
            // Add subscription metadata if present (only for cases beyond free tier)
            ...(subscriptionMetadata && { subscriptionMetadata }),
            timestamp: serverTimestamp(),
          };

          // Check case limit before adding
          const limitResult = await featureGatingService.checkCaseLimitBeforeExport(
            user.uid,
            1,
            caseStatus === "disposed" ? "disposed" : "pending"
          );

          if (!limitResult.canProceed) {
            setCaseLimitData({
              currentCount: limitResult.currentCount,
              maxLimit: limitResult.maxLimit,
              planName: limitResult.planName
            });
            setShowCaseLimitDialog(true);
            break;
          }

          // Add to Firestore
          const caseRef = await addDoc(collection(db, caseStatus), caseDataToExport);
          console.log("Case added to Firestore:", caseRef.id);

          // Mirror the order PDFs into the case's Files tab under an "Orders"
          // folder (named `<OrderDate>_order.pdf`).
          await saveOrderPdfsToFiles(ordersWithPdfs, caseRef.id, caseNo, caseStatus, user.uid);

          // Get FCM token
          let fcmToken = "";
          try {
            console.log("Attempting to get FCM token...");
            fcmToken = await getToken(messaging, {
              vapidKey: "BOSi0E-WalUbqOuB5su9XW2J39ot0D4x-fSPRYo_AxMVloqe7jIkpYXf5J007lXs1D_ZZB9YH1Hc0X-7jQrqR38",
            });
            console.log("FCM token obtained:", fcmToken);
          } catch (err) {
            console.error("Error getting FCM token:", err);
            console.warn("Could not get FCM token. Push notifications might not work:", err.message);
          }

          // Schedule notification if hearing date exists AND is in the future
          if (nextHearingDate) {
            try {
              const eventDateStr = nextHearingDate.split("/").reverse().join("-");
              const eventDate = new Date(eventDateStr);
              if (isNaN(eventDate.getTime())) {
                console.warn("Invalid hearing date format:", nextHearingDate);
              } else {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const hearingDay = new Date(eventDate);
                hearingDay.setHours(0, 0, 0, 0);

                if (hearingDay < today) {
                  console.log("Skipping event creation — next hearing date is in the past:", nextHearingDate);
                } else {
                  const caseNoForEvent = caseDetails?.caseDetails?.["Registration Number"] || "";

                  // Event 1: 8:00 AM IST on the next hearing date (visible)
                  const reminderDate = new Date(eventDate);
                  reminderDate.setHours(8, 0, 0, 0);

                  // Event 2: 6:00 PM IST on the next hearing date (hidden)
                  const eventTimeIST = new Date(eventDate);
                  eventTimeIST.setHours(18, 0, 0, 0);

                  const now = new Date();

                  if (reminderDate > now) {
                    const reminderEventData = {
                      "Case No": caseNoForEvent,
                      date: reminderDate.toISOString().split("T")[0],
                      id: Math.floor(Math.random() * 90000) + 10000,
                      mobile: userMobile || null,
                      time: "08:00 AM",
                      time1: reminderDate.getTime(),
                      title: `${partyName} VS ${adverseParty}`,
                      description: `Hearing Reminder : Case No. - ${caseNoForEvent}`,
                      isScheduled: reminderDate > now,
                      status: caseStatus,
                      eventType: "reminder",
                    };
                    const reminderEventRef = await addDoc(collection(db, "lawyers", user.uid, "events"), reminderEventData);

                    const reminderDoc = {
                      eventId: reminderEventRef.id,
                      reminderTime: reminderDate,
                      eventTitle: `Hearing Reminder : Case No. - ${caseNoForEvent}`,
                      caseNo: caseNoForEvent,
                      caseId: caseRef.id,
                      collection: caseStatus,
                      status: "pending",
                      createdAt: new Date(),
                      scheduledBy: user.uid,
                      recipientId: user.uid,
                    };
                    await addDoc(collection(db, "eventReminders"), reminderDoc);
                  }

                  if (eventTimeIST > now) {
                    const mainEventData = {
                      "Case No": caseNoForEvent,
                      date: eventDate.toISOString().split("T")[0],
                      id: Math.floor(Math.random() * 90000) + 10000,
                      mobile: userMobile || null,
                      time: "06:00 PM",
                      time1: eventTimeIST.getTime(),
                      title: `Update Next Hearing: ${partyName} VS ${adverseParty}`,
                      description: `Case No. - ${caseNoForEvent}`,
                      isScheduled: eventTimeIST > now,
                      status: caseStatus,
                      eventType: "updateReminder",
                      isHidden: true,
                    };
                    const mainEventRef = await addDoc(collection(db, "lawyers", user.uid, "events"), mainEventData);

                    const mainReminderDoc = {
                      eventId: mainEventRef.id,
                      reminderTime: eventTimeIST,
                      eventTitle: `Update Next Hearing : ${caseNoForEvent}`,
                      caseNo: caseNoForEvent,
                      caseId: caseRef.id,
                      collection: caseStatus,
                      status: "pending",
                      createdAt: new Date(),
                      scheduledBy: user.uid,
                      recipientId: user.uid,
                    };
                    await addDoc(collection(db, "eventReminders"), mainReminderDoc);
                  }
                }
              }
            } catch (notificationError) {
              console.error("Notification setup error:", notificationError);
            }
          }

          successfulExports++;

          // Increment pending count after adding a pending case
          if (caseStatus === "pending") {
            currentPendingCount++;
          }

          // Update export progress
          setExportProgress((prev) => ({
            ...prev,
            exported: prev.exported + 1,
            currentCase: `Completed: ${caseItem.case_no}`
          }));

          // Small delay to make progress visible
          await new Promise(resolve => setTimeout(resolve, 100));
        }

      } catch (caseError) {
        console.error(`Error processing case ${caseItem?.case_no}:`, caseError);
        failedExports++;
        failedCasesList.push(caseItem?.case_no || 'Unknown');

        // Update progress even for failed cases
        setExportProgress((prev) => ({
          ...prev,
          exported: prev.exported + 1,
          failedCases: [...prev.failedCases, caseItem?.case_no || 'Unknown'],
          currentCase: `Failed: ${caseItem?.case_no}`
        }));

        // Small delay to make progress visible
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }

    // If all selected cases are duplicates, show modal
    if (duplicatesFound.length === selectedCaseItems.length) {
      setExporting(false);
      setDuplicateCases(duplicatesFound);
      setShowDuplicateModal(true);
      setExportProgress({
        total: 0,
        exported: 0,
        currentCase: null,
        startTime: null,
        failedCases: []
      });
      return;
    }

    // Show completion message briefly before closing
    await new Promise(resolve => setTimeout(resolve, 500));

    // Show results
    if (successfulExports > 0 || duplicatesFound.length > 0) {
      const message = `Successfully exported ${successfulExports} cases. ${duplicatesFound.length} duplicates skipped. ${failedExports} cases failed.`;
      console.log(message);

      if (duplicatesFound.length > 0 && successfulExports === 0 && failedExports === 0) {
        // All were duplicates
        setDuplicateCases(duplicatesFound);
        setShowDuplicateModal(true);
      } else if (failedExports > 0 && failedCasesList.length > 0) {
        // Some succeeded, some failed — show combined message in one dialog
        setFailedExportMessage(
          `${successfulExports} case(s) exported successfully.\n\n${failedExports} case(s) not exported due to no data found from public sources: ${failedCasesList.join(", ")}`
        );
        setShowFailedExportModal(true);
      } else {
        // All successful
        showInfo();
      }
    } else if (failedExports > 0) {
      setFailedExportMessage(
        `${failedExports} case(s) could not be exported — no data found from public sources.\n\nFailed cases: ${failedCasesList.join(", ")}`
      );
      setShowFailedExportModal(true);
    }

  } catch (error) {
    console.error("Export error:", error);
    alert(`Error exporting cases: ${error.message}`);
  } finally {
    setExporting(false);
    // Reset progress
    setExportProgress({
      total: 0,
      exported: 0,
      currentCase: null,
      startTime: null,
      failedCases: []
    });
  }
};

  const handleCaseClick = async (caseItem) => {
    // Prevent overlapping clicks
    if (loadingCaseCino) return;

    // Existing case click logic remains the same
    if (!location.state || !location.state.searchParams) {
      console.error("Search parameters are missing");
      return;
    }
    const
      { court_code,
        state_code,
        court_complex_code

      } =
        location.state.searchParams;

    // Response cookies have updated HCSERVICES_SESSID, but JSESSION comes from original captcha cookies
    const responseCookies = location.state.data?.cookies || location.state.searchParams.cookies;
    const originalCookies = location.state.searchParams.cookies;
    const hcservices_sessid = responseCookies?.HCSERVICES_SESSID || originalCookies?.HCSERVICES_SESSID || "";
    const jsession_value = originalCookies?.JSESSION || originalCookies?.JSESSIONID || responseCookies?.JSESSION || responseCookies?.JSESSIONID || "";

    console.log("hcservices_sessid:", hcservices_sessid);
    console.log("jsession_value:", jsession_value);
    console.log("responseCookies:", responseCookies);
    console.log("originalCookies:", originalCookies);

    setLoadingCaseCino(getCaseKey(caseItem));

    try {
      const detailsPayload = {
        hcservices_sessid,
        jsession_value,
        court_code: getHighCourtDetailsCourtCode(location.state.searchParams),
        state_code: state_code,
        court_complex_code: court_complex_code,
        case_no: getHighCourtDetailsCaseNumber(caseItem),
        cino: caseItem.cino,
        appFlag: "",
      };

      // Store case details payload in localStorage
      localStorage.setItem('hc_case_details_payload', JSON.stringify(detailsPayload));

      const response = await apiRequest(
        "/case/details/highcourt",
        detailsPayload
      );

      // Check for error response from API
      if (response?.error) {
        const displayError = response.code === "HC_PORTAL_INVALID_RESPONSE"
          ? "No data found from public sources"
          : response.error;
        toast.error(displayError, { duration: 5000 });
        return;
      }

      navigate("/portal/caseDetails", {
        state: {
          caseDetails: response.data,
          courtCode: court_code,
          stateCode: state_code,
          courtComplexCode: court_complex_code,
          status: location.state?.searchParams?.status || location.state?.searchParams?.f?.toLowerCase() || "pending",
          petresName: caseItem.pet_name || caseItem.extra_party,
          cino: caseItem.cino,
          caseNumber: caseItem.case_no,
          cookies: response.cookies || responseCookies,
        },
      });
    } catch (error) {
      console.error("Failed to fetch case details:", error);
      const errorCode = error?.response?.data?.code;
      const errorMessage = errorCode === "HC_PORTAL_INVALID_RESPONSE"
        ? "No data found from public sources"
        : error?.response?.data?.error || error?.message || "Failed to fetch case details. Please try again.";
      toast.error(errorMessage, { duration: 5000 });
    } finally {
      setLoadingCaseCino(null);
    }
  };

  // Loading state
  if (isLoadingData) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto mb-4"></div>
            <p className="text-slate-500 text-lg">Loading case data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!caseData) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <p className="text-slate-500 text-lg">No case data available</p>
            <Link to="/portal/fetchfromhighcourt" className="text-blue-600 hover:underline mt-2 inline-block">
              Return to Search
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <div className="px-4 sm:px-6 lg:px-8 py-4">
        {/* Breadcrumb */}
        <div className="mb-4 flex items-center gap-1.5 text-sm">
          <Link to="/portal/findCasesOptions" className="text-slate-500 hover:text-slate-800">Find Cases</Link>
          <ChevronRight className="h-4 w-4 text-slate-400" />
          <Link to="/portal/fetchfromhighcourt" className="text-slate-500 hover:text-slate-800">High Court</Link>
          <ChevronRight className="h-4 w-4 text-slate-400" />
          <span className="font-medium text-slate-800">Results</span>
        </div>

        {/* Stepper */}
        <div className="mb-4"><FindCaseStepper current={3} /></div>

        {/* Summary stat cards */}
        {cases.length > 0 && (
          <div className="mb-4 grid grid-cols-2 gap-4 lg:grid-cols-4">
            {[
              { label: "Total Found", value: statusCounts.total, Icon: Users, tint: "bg-indigo-50 text-indigo-500" },
              { label: "Selected", value: selectedCases.size, Icon: CheckSquare, tint: "bg-blue-50 text-blue-500" },
              { label: "Pending", value: statusCounts.pending, Icon: Clock, tint: "bg-green-50 text-green-500" },
              { label: "Disposed", value: statusCounts.disposed, Icon: CircleCheck, tint: "bg-red-50 text-red-500" },
            ].map(({ label, value, Icon, tint }) => (
              <div
                key={label}
                className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
              >
                <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${tint}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <p className="text-2xl font-bold leading-tight text-slate-900">{value}</p>
                  <p className="truncate text-xs text-slate-500">{label}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Enhanced Export Progress Modal */}
        {exporting && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 backdrop-blur-sm">
            <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl transform transition-all duration-300 scale-100">
              <div className="text-center">
                {/* Header with icon */}
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                    <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                    </svg>
                  </div>
                </div>
                
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Exporting Your Cases
                </h3>
                
                <p className="text-sm text-gray-600 mb-6">
                  Please wait while we process and export your selected cases...
                </p>

                {/* Enhanced Progress Bar */}
                <div className="relative mb-6">
                  <div className="w-full bg-gray-200 rounded-full h-6 overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-500 ease-out relative"
                      style={{
                        width: exportProgress.total > 0
                          ? `${(exportProgress.exported / exportProgress.total) * 100}%`
                          : '0%'
                      }}
                    >
                      {/* Animated stripe pattern */}
                      <div className="absolute inset-0 bg-white bg-opacity-20 animate-pulse"></div>
                      <div 
                        className="absolute inset-0 opacity-30"
                        style={{
                          backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,0.1) 10px, rgba(255,255,255,0.1) 20px)',
                          animation: 'slide 1s linear infinite'
                        }}
                      ></div>
                    </div>
                  </div>
                  
                  {/* Percentage label on progress bar */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-xs font-bold text-gray-700">
                      {exportProgress.total > 0
                        ? `${Math.round((exportProgress.exported / exportProgress.total) * 100)}%`
                        : '0%'}
                    </span>
                  </div>
                </div>

                {/* Progress Statistics */}
                <div className="bg-blue-50 rounded-lg p-4 mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-600">Cases Exported:</span>
                    <span className="text-lg font-bold text-blue-600">
                      {exportProgress.exported} / {exportProgress.total}
                    </span>
                  </div>
                  
                  {exportProgress.exported > 0 && exportProgress.startTime && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-600">Estimated Time:</span>
                      <span className="text-sm font-semibold text-gray-700">
                        {(() => {
                          const elapsed = Date.now() - exportProgress.startTime;
                          const avgTimePerCase = elapsed / exportProgress.exported;
                          const remaining = exportProgress.total - exportProgress.exported;
                          const estimatedMs = remaining * avgTimePerCase;
                          const estimatedSeconds = Math.ceil(estimatedMs / 1000);
                          if (estimatedSeconds < 60) return `${estimatedSeconds}s remaining`;
                          return `${Math.ceil(estimatedSeconds / 60)}m remaining`;
                        })()}
                      </span>
                    </div>
                  )}
                  
                  {exportProgress.failedCases.length > 0 && (
                    <div className="mt-2 text-xs text-red-600">
                      Failed: {exportProgress.failedCases.length} case(s)
                    </div>
                  )}
                </div>

                {/* Current Action */}
                {exportProgress.currentCase && (
                  <div className="flex items-center justify-center space-x-2 mb-4">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                      <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                      <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                    </div>
                    <span className="text-sm text-gray-600 truncate max-w-xs">
                      {exportProgress.currentCase}
                    </span>
                  </div>
                )}

                {/* Info message */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-xs text-yellow-800 flex items-start">
                    <svg className="w-4 h-4 mr-1 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    Please do not close this window or navigate away until the export is complete.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add keyframe animation for sliding stripes */}
        <style>{`
          @keyframes slide {
            0% {
              transform: translateX(0);
            }
            100% {
              transform: translateX(20px);
            }
          }
        `}</style>

        {/* Export Progress Banner (visible at top when exporting) */}
        {exporting && (
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 rounded-lg mb-4 shadow-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                <div>
                  <span className="font-medium">
                    Exporting cases... 
                  </span>
                  <span className="ml-2 text-sm opacity-90">
                    {exportProgress.exported}/{exportProgress.total} completed
                  </span>
                </div>
              </div>
              <div className="text-lg font-bold">
                {exportProgress.total > 0
                  ? `${Math.round((exportProgress.exported / exportProgress.total) * 100)}%`
                  : '0%'}
              </div>
            </div>
          </div>
        )}

        {/* Cases Table */}
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white text-slate-800 shadow-sm">
          {cases.length === 0 ? (
            <div className="py-12 text-center">
              <svg className="mx-auto mb-4 h-16 w-16 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-lg font-medium text-slate-700">No cases found</p>
              <p className="mt-1 text-sm text-slate-400">Try adjusting your search criteria</p>
            </div>
          ) : (
            <>
              {/* Toolbar — search + bulk actions */}
              <div className="flex flex-col gap-3 border-b border-slate-100 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex flex-1 items-center gap-3">
                  <div className="relative w-full max-w-sm">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => handleSearchChange(e.target.value)}
                      placeholder="Search in results..."
                      className="h-10 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-9 text-sm text-slate-900 placeholder-slate-400 outline-none transition-colors focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/30"
                    />
                    {searchQuery && (
                      <button
                        type="button"
                        onClick={handleClearSearch}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700"
                        title="Clear search"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                  {totalPages > 1 && filteredCases.length > 0 && (
                    <button
                      type="button"
                      onClick={handlePageSelectAll}
                      className="hidden h-10 shrink-0 rounded-lg border border-slate-200 px-4 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50 md:block"
                    >
                      {currentCases.length > 0 && currentCases.every((c) => selectedCases.has(getCaseKey(c)))
                        ? "Deselect Page"
                        : "Select Page"}
                    </button>
                  )}
                </div>

                <div className="flex shrink-0 items-center gap-3">
                  {selectedCases.size > 0 && (
                    <span className="text-sm text-slate-500">
                      {selectedCases.size} selected
                    </span>
                  )}
                  <button
                    onClick={exportSelectedCases}
                    className={`flex h-10 items-center gap-2 rounded-lg px-5 text-sm font-semibold transition-colors ${
                      exporting || selectedCases.size === 0
                        ? "cursor-not-allowed bg-slate-100 text-slate-400"
                        : "bg-yellow-500 text-slate-900 hover:bg-yellow-400"
                    }`}
                    disabled={exporting || selectedCases.size === 0}
                  >
                    {exporting ? (
                      <>
                        <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Exporting ({exportProgress.exported}/{exportProgress.total})
                      </>
                    ) : (
                      <>
                        <Download className="h-4 w-4" />
                        Export Selected ({selectedCases.size})
                      </>
                    )}
                  </button>
                </div>
              </div>

              {filteredCases.length === 0 ? (
                <div className="py-12 text-center">
                  <p className="text-base font-medium text-slate-700">No cases match your search</p>
                  <button
                    type="button"
                    onClick={handleClearSearch}
                    className="mt-2 text-sm font-medium text-yellow-600 hover:underline"
                  >
                    Clear search
                  </button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="data-table w-full border-collapse">
                    <thead>
                      <tr className="border-b border-slate-200 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                        <th className="w-12 px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectAll}
                            onChange={handleSelectAll}
                            className="h-4 w-4 cursor-pointer rounded border-slate-300 accent-yellow-500"
                            id="selectAll"
                            disabled={filteredCases.length === 0}
                            title="Select all"
                          />
                        </th>
                        <th className="px-4 py-3">Case No.</th>
                        <th className="px-4 py-3">Petitioner</th>
                        <th className="px-4 py-3">Respondent</th>
                        <th className="px-4 py-3">Case Type</th>
                        <th className="px-4 py-3">Status</th>
                        <th className="px-4 py-3">CNR</th>
                        <th className="w-16 px-4 py-3 text-center">Action</th>
                      </tr>
                    </thead>

                    <tbody>
                      {currentCases.map((caseItem) => {
                        const rowKey = getCaseKey(caseItem);
                        const isRowLoading = loadingCaseCino === rowKey;
                        const rowClickable = !loadingCaseCino;
                        const isDisposed = caseItem.date_of_decision != null;
                        const openCase = () => rowClickable && handleCaseClick(caseItem);
                        const cellClass = `px-4 py-3.5 text-sm ${
                          rowClickable ? "cursor-pointer" : "cursor-wait"
                        }`;
                        return (
                          <tr
                            key={rowKey}
                            className={`border-b border-slate-100 transition-colors duration-150 ${
                              selectedCases.has(rowKey) ? "bg-yellow-50/60" : "hover:bg-slate-50"
                            } ${loadingCaseCino && !isRowLoading ? "opacity-50" : ""}`}
                          >
                            <td className="px-4 py-3.5">
                              <input
                                type="checkbox"
                                checked={selectedCases.has(rowKey)}
                                onChange={() => handleCaseSelect(caseItem)}
                                className="h-4 w-4 cursor-pointer rounded border-slate-300 accent-yellow-500"
                              />
                            </td>
                            <td className={`${cellClass} font-medium text-slate-900`} onClick={openCase}>
                              <div className="flex items-center gap-2">
                                {isRowLoading && (
                                  <svg className="h-4 w-4 animate-spin text-yellow-500" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                )}
                                <span>{caseItem.case_no}</span>
                              </div>
                            </td>
                            <td className={`${cellClass} text-slate-600`} onClick={openCase}>
                              {caseItem.pet_name || caseItem.extra_party}
                            </td>
                            <td className={`${cellClass} text-slate-600`} onClick={openCase}>
                              {caseItem.res_name || caseItem.lres_name || caseItem.lextra_party || "—"}
                            </td>
                            <td className={`${cellClass} text-slate-600`} onClick={openCase}>
                              {caseItem.type_name}
                            </td>
                            <td className={cellClass} onClick={openCase}>
                              <span
                                className={`${STATUS_PILL_BASE} ${
                                  STATUS_PILL_SOFT[isDisposed ? "disposed" : "pending"]
                                }`}
                              >
                                {isDisposed ? "Disposed" : "Pending"}
                              </span>
                            </td>
                            <td className={`${cellClass} text-xs text-slate-500`} onClick={openCase}>
                              {caseItem.cino}
                            </td>
                            <td className="px-4 py-3.5 text-center">
                              <button
                                type="button"
                                onClick={openCase}
                                disabled={!rowClickable}
                                title="View case details"
                                className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 disabled:cursor-wait"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Footer — result range + pagination */}
              {filteredCases.length > 0 && (
                <div className="flex flex-col items-center gap-4 border-t border-slate-100 px-4 py-4 sm:flex-row sm:justify-between">
                  <p className="text-sm text-slate-500">
                    Showing {indexOfFirstCase + 1} to {Math.min(indexOfLastCase, filteredCases.length)} of{" "}
                    {filteredCases.length} results
                  </p>

                  {totalPages > 1 && (
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={handlePreviousPage}
                        disabled={currentPage === 1}
                        className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                        title="Previous page"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </button>

                      {currentPage > 3 && (
                        <>
                          <button
                            onClick={() => handlePageChange(1)}
                            className="h-9 min-w-[2.25rem] rounded-lg border border-slate-200 px-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50"
                          >
                            1
                          </button>
                          {currentPage > 4 && <span className="px-1 text-slate-400">...</span>}
                        </>
                      )}

                      {getPageNumbers().map((number) => (
                        <button
                          key={number}
                          onClick={() => handlePageChange(number)}
                          className={`h-9 min-w-[2.25rem] rounded-lg px-2 text-sm font-medium transition-colors ${
                            currentPage === number
                              ? "bg-yellow-500 text-slate-900"
                              : "border border-slate-200 text-slate-600 hover:bg-slate-50"
                          }`}
                        >
                          {number}
                        </button>
                      ))}

                      {currentPage < totalPages - 2 && (
                        <>
                          {currentPage < totalPages - 3 && <span className="px-1 text-slate-400">...</span>}
                          <button
                            onClick={() => handlePageChange(totalPages)}
                            className="h-9 min-w-[2.25rem] rounded-lg border border-slate-200 px-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50"
                          >
                            {totalPages}
                          </button>
                        </>
                      )}

                      <button
                        onClick={handleNextPage}
                        disabled={currentPage === totalPages}
                        className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                        title="Next page"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <InfoModal
        isOpen={isInfoModalOpen}
        onClose={closeInfoModal}
        title="Export Successful"
        message={`All selected cases exported successfully!

Default reminder has been set to 1 day before the next hearing date for each case.`}
      />

      <InfoModal
        isOpen={showFailedExportModal}
        onClose={() => setShowFailedExportModal(false)}
        title="Export Summary"
        message={failedExportMessage}
      />

      {/* Duplicate Cases Modal */}
      {showDuplicateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl">
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Duplicate Cases Detected
              </h3>
              
              <p className="text-gray-600 mb-6">
                All selected cases already exist in your profile. They cannot be exported again.
              </p>
              
              <div className="max-h-40 overflow-y-auto mb-6 bg-gray-50 rounded-lg p-3">
                {duplicateCases.map((item, index) => (
                  <div key={index} className="text-sm text-gray-700 py-1">
                    {item.caseNo} - Already in {item.collection}
                  </div>
                ))}
              </div>
              
              <button
                onClick={() => {
                  setShowDuplicateModal(false);
                  setDuplicateCases([]);
                }}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700 transition-all"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Case Limit Dialog */}
      <CaseLimitDialog
        isOpen={showCaseLimitDialog}
        onClose={() => setShowCaseLimitDialog(false)}
        currentCount={caseLimitData.currentCount}
        maxLimit={caseLimitData.maxLimit}
        planName={caseLimitData.planName}
      />
    </div>
  );
}

export default CaseListScreen;
